/* eslint-disable require-await */
/* hooks/usePatientPlans.ts */
/* eslint-disable no-void */
import { useCallback, useEffect, useRef, useState } from 'react';

import { getMyPlanSummary } from '../services/patient_plan/services';
import type { GroupSummary, PlanDecision } from '../services/patient_plan/types/types';

type State = {
  loading: boolean;
  error: string | null;
  group: GroupSummary | null;
  decision: PlanDecision | null;
};

let cache: {
  group: GroupSummary | null;
  decision: PlanDecision | null;
  fetchedAt: number;
} | null = null;

let inflight: Promise<GroupSummary | null> | null = null;

const TTL = 60_000;

function isFresh(c: typeof cache): boolean {
  return Boolean(c && Date.now() - c.fetchedAt < TTL);
}

async function fetchOnce(): Promise<GroupSummary | null> {
  if (inflight) return inflight;
  inflight = getMyPlanSummary()
    .then((group) => {
      cache = {
        group: group ?? null,
        decision: null,
        fetchedAt: Date.now(),
      };
      return cache.group;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

const DISABLED_STATE: State = {
  loading: false,
  error: null,
  group: null,
  decision: null,
};

/** Public hook */
export function usePatientPlan(enabled = true): {
  loading: boolean;
  error: string | null;
  group: GroupSummary | null;
  decision: PlanDecision | null;
  refresh: () => void;
} {
  const [state, setState] = useState<State>(() => {
    if (!enabled) return DISABLED_STATE;
    if (isFresh(cache)) {
      return {
        loading: false,
        error: null,
        group: cache!.group,
        decision: cache!.decision,
      };
    }
    return { loading: true, error: null, group: null, decision: null };
  });

  // mounted ref to avoid setState on unmounted components
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      setState(DISABLED_STATE);
      return;
    }

    if (isFresh(cache)) {
      if (!mounted.current) return;
      setState({
        loading: false,
        error: null,
        group: cache!.group,
        decision: cache!.decision,
      });
      return;
    }

    setState((s) => ({ ...s, loading: !s.group, error: null }));

    try {
      const group = await fetchOnce();
      if (!mounted.current) return;
      setState({
        loading: false,
        error: null,
        group,
        decision: cache?.decision ?? null,
      });
    } catch (err: any) {
      if (!mounted.current) return;
      setState((s) => ({
        ...s,
        loading: false,
        error: err?.response?.data?.message || err?.message || 'Failed to load patient plan.',
      }));
    }
  }, [enabled]);

  const loadedOnceRef = useRef(false);
  useEffect(() => {
    if (loadedOnceRef.current) return;
    loadedOnceRef.current = true;
    void load();
  }, [load]);

  const lastFocusRef = useRef(0);
  useEffect(() => {
    const onFocus = (): void => {
      const now = Date.now();
      if (!isFresh(cache) && now - lastFocusRef.current > 5_000) {
        lastFocusRef.current = now;
        void load();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const refresh = useCallback(() => {
    if (cache) cache.fetchedAt = 0;
    void load();
  }, [load]);

  return { ...state, refresh };
}
